import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Heading, Link, Skeleton } from '@razorpay/blade/components';
import { generateUserId } from 'utils/commons';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { fetchLoggedInUser } from 'reducers/loggedInUser/actionCreators';
import { CustomFieldsDisplay } from './CustomFieldsDisplay';
import { getEditableFields } from './utils';
import { EmployeeOtherInfoWidgetProps } from './types';
import { useEmployeeCustomFields } from './hooks/useEmployeeCustomFields';
import { useCurrentRole } from './hooks/useCurrentRole';
import { isForbiddenError } from './errors';
import { routePaths } from 'components/Routes/data';

/**
 * EMPLOYEE_OTHER_INFO widget — mounts on the legacy employeeDetail.php page via createReactWidget.
 *
 * Wrapper decision: NO extra QueryClientProvider / Redux Provider is needed here.
 * The react-v1-widgets.tsx harness wraps every widget with:
 *   QueryClientProvider → BrowserRouter → Provider (Redux store) → BladeProvider
 * so all react-query hooks, useSelector, and Blade components work out of the box.
 * Adding an extra QueryClientProvider would create an isolated child cache and break
 * cache sharing with other widgets on the same page.
 *
 * userId (number) comes from PHP via createReactWidget props.
 * The BFF expects HRIS-format user ids ("user_<n>"), so we prefix via generateUserId.
 * The Edit link href uses the bare numeric id per the route convention
 * (/employee/:userId/other-information/edit) — the edit page prefixes internally.
 *
 * Viewer role: legacy pages never boot the main app, so Redux loggedInUser starts
 * empty and every policied field would compute HIDDEN. The widget dispatches
 * fetchLoggedInUser itself (the harness store runs the shared root saga) and shows
 * a skeleton until the role lands. Server-authoritative via the get-me API; HRIS
 * still enforces access on every read/write regardless.
 */
export const EmployeeOtherInfoWidget = ({ userId }: EmployeeOtherInfoWidgetProps) => {
  const dispatch = useDispatch();
  const { roleId } = useCurrentRole();
  const isFetchingUser = useSelector(loggedInUserSelectors.isFetching);
  const hasUserError = useSelector(loggedInUserSelectors.hasError);
  const requestedUserFetch = useRef(false);

  useEffect(() => {
    if (roleId === null && !requestedUserFetch.current) {
      requestedUserFetch.current = true;
      dispatch(fetchLoggedInUser());
    }
  }, [roleId, dispatch]);

  const { definitions, values, isLoading, isError, error, refetch } = useEmployeeCustomFields(
    generateUserId(String(userId)),
  );

  const retryUserFetch = () => {
    dispatch(fetchLoggedInUser());
  };

  if (roleId === null && hasUserError) {
    return (
      <Alert
        color="negative"
        title="Could not load other information"
        description="Please retry."
        actions={{ primary: { text: 'Retry', onClick: retryUserFetch } }}
      />
    );
  }
  // Covers both the pre-dispatch render and the in-flight get-me call: without a
  // role every policied field computes HIDDEN, which must not flash as "empty".
  if (roleId === null || isFetchingUser || isLoading) {
    return <Skeleton height="120px" width="100%" />;
  }
  if (isError) {
    // Permission denied (e.g. viewing another employee without VIEW_EMPLOYEES, or
    // all fields HIDDEN for this role) is not a transient outage — show an
    // access-denied state WITHOUT a Retry button.
    if (isForbiddenError(error)) {
      return (
        <Alert
          color="notice"
          title="Access restricted"
          description="You don't have access to view these details."
        />
      );
    }
    return (
      <Alert
        color="negative"
        title="Could not load other information"
        description="Please retry."
        actions={{ primary: { text: 'Retry', onClick: refetch } }}
      />
    );
  }
  if (definitions.length === 0) return null;

  const canEdit = getEditableFields(definitions).length > 0;
  const editHref = routePaths.employeeCustomFields.otherInfoEdit.replace(':userId', String(userId));

  return (
    // Heading + bordered row container mirror SalaryComponentsEntrypoint on settings.php.
    <Box marginBottom="spacing.10">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="spacing.4"
        paddingLeft="spacing.0">
        <Heading weight="semibold" size="medium" color="surface.text.gray.normal">
          Other Information
        </Heading>
        {canEdit && <Link href={editHref}>Edit</Link>}
      </Box>
      <CustomFieldsDisplay definitions={definitions} values={values} />
    </Box>
  );
};
